import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { ProfileData } from '../types';
import { apiService } from '../services/apiService';
import { LogoIcon, HomeIcon, ArrowLeftIcon, ArrowRightWhiteIcon, MagicIcon } from './icons';
import { friendlyAuthError } from '../utils/authErrors';

const FormInput = ({ label, value, onChange, name, placeholder, type = 'text', disabled = false }: { label: string; value: string; onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void; name: keyof ProfileData | 'referredByFirstName' | 'referredByLastName' | 'referredByEmail'; placeholder?: string; type?: string; disabled?: boolean; }) => (
    <div className="flex flex-col items-start gap-1.5 flex-1 w-full">
        <label className="text-grayscale-1100 font-grotesk text-xs font-normal">{label}</label>
        <div className="flex h-11 py-3 px-2.5 items-center gap-2.5 self-stretch rounded-md border-primary-300 border-[0.5px] bg-white">
            <input
                type={type}
                placeholder={placeholder}
                name={name}
                value={value}
                onChange={onChange}
                disabled={disabled}
                className="flex-1 bg-transparent text-grayscale-1300 font-grotesk text-sm focus:outline-none"
            />
        </div>
    </div>
);

interface CompleteProfilePageProps {
    userEmail: string;
    onSkip: () => void;
    onComplete: (updatedProfile: ProfileData) => void;
    initialProfileData: ProfileData;
    onLogout: () => void;
}

const CompleteProfilePage: React.FC<CompleteProfilePageProps> = ({ userEmail, onSkip, onComplete, initialProfileData, onLogout }) => {
    const [profileData, setProfileData] = useState<ProfileData>(initialProfileData);
    const [fixingSummary, setFixingSummary] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    useEffect(() => {
        const { firstName, lastName, country, companyName, designation, linkedin, summary } = profileData;
        if (firstName && lastName && country && companyName && designation && linkedin && summary) {
            setIsFormValid(true);
        } else {
            setIsFormValid(false);
        }
    }, [profileData]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        
        // Apply 500 character limit for summary field when user is typing
        if (name === 'summary' && value.length > 500) {
            return; // Don't allow user to type more than 500 characters
        }
        
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handleFixSummary = async () => {
        setFixingSummary(true);
        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                console.error("User not logged in");
                alert("Please log in to use the Fix Text feature.");
                return;
            }
            
            // Use enhanced API with LinkedIn context
            const response = await apiService.enhanceText(
                profileData.summary,
                'professional_summary',
                userId,
                profileData.linkedin || undefined,
                undefined // No company LinkedIn available in CompleteProfilePage
            );
            
            if (response.success && response.data) {
                setProfileData(prev => ({ ...prev, summary: response.data.enhanced_text }));
            } else {
                console.error("Failed to enhance text:", response.error);
                alert(friendlyAuthError(response.error, 'Failed to enhance text. Please try again.'));
            }
        } catch (error) {
            console.error("Error fixing summary:", error);
            alert("Network error occurred. Please check your connection and try again.");
        } finally {
            setFixingSummary(false);
        }
    };

    const handleComplete = () => {
        if(isFormValid) {
            onComplete(profileData);
        }
    }

    return (
        <div className="min-h-screen w-full flex flex-col p-4 items-center gap-6 bg-gradient-to-b from-[rgba(249,247,243,0.9)] to-[rgba(249,247,243,0.9)] bg-white font-grotesk text-grayscale-1300">
            <header className="w-full flex justify-between items-center flex-shrink-0">
                <div className="flex w-12 h-12 p-1 justify-center items-center">
                    <div className="flex w-[41px] h-[41px] p-[1.117px_1.486px_1.283px_0.914px] justify-center items-center">
                        <LogoIcon />
                    </div>
                </div>
                <button onClick={onLogout} className="flex w-12 h-12 p-3 flex-col justify-center items-center gap-2 rounded-xl border border-primary-200 bg-white shadow-custom-light">
                    <HomeIcon />
                </button>
            </header>
            <main className="flex-1 w-full flex flex-col md:flex-row justify-center items-stretch gap-6 rounded-2xl max-w-7xl">
                <div className="w-full md:w-[336px] flex flex-col items-center gap-4 flex-shrink-0 rounded-2xl shadow-custom-light p-6 bg-white">
                    <div className="flex flex-col justify-center items-start gap-2 self-stretch">
                        <h1 className="text-grayscale-1300 text-2xl font-medium">Hi {profileData.firstName || 'there'}!</h1>
                        <p className="text-grayscale-1100 font-grotesk text-sm leading-tight self-stretch">Please check your details before completing your account. You can also modify this later.</p>
                    </div>
                    <div className="flex p-6 flex-col items-start gap-4 flex-1 self-stretch rounded-2xl bg-primary-100/50">
                        <h2 className="text-main-text font-bold">A Note from the Founder</h2>
                        <p className="text-grayscale-1100 font-grotesk text-sm leading-normal">
                            A warm welcome to the future of tech transfer. Our mission is to democratise access to university innovations and researchers.
                            <br/><br/>
                            As an early adopter, I personally look forward to your feedback and input as we progress on our mission.
                            <br/><br/>
                            May the Cosmos be with you,
                            <br/><br/>
                            Parag
                        </p>
                    </div>
                </div>
                <div className="flex-1 min-h-0 flex flex-col p-6 md:p-10 rounded-2xl border border-primary-400 shadow-custom-light bg-white">
                    <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar pr-2 -mr-2">
                        <div className="flex flex-col gap-6">
                            <FormInput label="Email" name="email" value={profileData.email} onChange={handleChange} disabled />
                            <div className="flex flex-col md:flex-row gap-4">
                                <FormInput label="First Name" name="firstName" value={profileData.firstName} onChange={handleChange} placeholder="John" />
                                <FormInput label="Last Name" name="lastName" value={profileData.lastName} onChange={handleChange} placeholder="Doe" />
                                <FormInput label="Country" name="country" value={profileData.country} onChange={handleChange} placeholder="India" />
                            </div>
                            <div className="flex flex-col md:flex-row gap-4">
                                 <FormInput label="Company Name" name="companyName" value={profileData.companyName} onChange={handleChange} placeholder="ABC Company" />
                                 <FormInput label="Designation" name="designation" value={profileData.designation} onChange={handleChange} placeholder="Corporate Innovation Manager" />
                                 <FormInput label="LinkedIn or alternative URL" name="linkedin" value={profileData.linkedin} onChange={handleChange} placeholder="_johndoe.linkedin" />
                            </div>
                             <div className="flex flex-col items-start gap-1.5 self-stretch">
                                <label className="text-grayscale-1100 font-grotesk text-xs font-bold">Professional Summary (AI Generated. This is used to personalise your search experience and for university connects)</label>
                                <div className="relative w-full">
                                    <textarea name="summary" value={profileData.summary} onChange={handleChange} className="w-full p-3 rounded-md border-[0.5px] border-primary-300 bg-alternate-bg text-grayscale-1200 text-sm font-grotesk focus:outline-none resize-y hide-scrollbar min-h-[100px]" />
                                    <div className="absolute bottom-2 right-2 flex items-center gap-2">
                                         <button onClick={handleFixSummary} disabled={fixingSummary} className="flex items-center gap-1.5 py-1 px-1.5 rounded bg-white shadow-custom-light">
                                            <span className="text-[#2C5695] text-[10px] font-grotesk italic">{fixingSummary ? 'Regenerating...' : 'Regenerate'}</span>
                                            <MagicIcon />
                                        </button>
                                        <span className={`text-[10px] font-grotesk italic font-light ${profileData.summary.length > 500 ? 'text-orange-600' : 'text-grayscale-900'}`}>
                                            {profileData.summary.length}/500 char {profileData.summary.length > 500 ? '(AI generated)' : ''}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-start gap-4 self-stretch">
                                <p className="self-stretch text-black font-grotesk text-sm font-medium leading-[180%] tracking-[-0.154px]">
                                    If Referred, let us know who were you referred by
                                </p>
                                <div className="flex flex-col md:flex-row items-start gap-4 self-stretch">
                                    <FormInput label="First Name" name="referredByFirstName" value={profileData.referredByFirstName || ''} onChange={handleChange} placeholder="Smith" />
                                    <FormInput label="Last Name" name="referredByLastName" value={profileData.referredByLastName || ''} onChange={handleChange} placeholder="Doe" />
                                    <FormInput label="Email" name="referredByEmail" value={profileData.referredByEmail || ''} onChange={handleChange} placeholder="smithdoe@gmail.com" type="email" />
                                </div>
                            </div>
                        </div>
                    </div>
                     <div className="w-full flex flex-col md:flex-row md:justify-end items-center gap-4 pt-6 flex-shrink-0">
                        <button onClick={onSkip} className="w-full md:w-auto flex py-2.5 px-4 justify-center items-center gap-2 rounded-full border border-primary-og-600 bg-white shadow-custom-light">
                            <span className="text-grayscale-1300 text-center font-grotesk text-sm font-medium">Skip & Explore Cosmos</span>
                        </button>
                        <button 
                            onClick={handleComplete}
                            disabled={!isFormValid}
                            className="w-full md:w-auto flex py-2.5 px-4 justify-center items-center gap-2 rounded-full bg-primary-og-600 shadow-custom-light text-white disabled:bg-primary-200"
                        >
                            <span className="text-center font-grotesk text-sm font-medium">Complete Account Creation</span>
                            <ArrowRightWhiteIcon />
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CompleteProfilePage;